import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  SearchBox,
  CommandBar,
  ICommandBarItemProps,
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  Modal,
  TextField,
} from '@fluentui/react';
import { IMengeneinheit, IUserPermissions } from '../../shared/models';
import { mengeneinheitenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import axios from 'axios';

export const MengeneinheitenListe: React.FC = () => {
  const { user } = useAuth();
  const [mengeneinheiten, setMengeneinheiten] = useState<IMengeneinheit[]>([]);
  const [filteredMengeneinheiten, setFilteredMengeneinheiten] = useState<IMengeneinheit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<IMengeneinheit[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);

  // Dialog States
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedMengeneinheit, setSelectedMengeneinheit] = useState<IMengeneinheit | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<IMengeneinheit>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMengeneinheiten();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterMengeneinheiten();
  }, [searchText, mengeneinheiten]);

  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadMengeneinheiten = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mengeneinheitenApi.getAll();
      setMengeneinheiten(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Mengeneinheiten');
    } finally {
      setLoading(false);
    }
  };

  const filterMengeneinheiten = () => {
    let filtered = [...mengeneinheiten];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((m) =>
        m.Einheit?.toLowerCase().includes(searchLower) ||
        m.Kurzform?.toLowerCase().includes(searchLower) ||
        m.Kennzahl?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredMengeneinheiten(filtered);
  };

  const isAdminOrEinkauf = useMemo(() => {
    if (!permissions) return false;
    return permissions.isAdmin || permissions.isEinkauf;
  }, [permissions]);

  const handleDeleteSingle = async (mengeneinheit: IMengeneinheit) => {
    if (!mengeneinheit.Id) return;

    try {
      setSaving(true);
      setError(null);
      await mengeneinheitenApi.delete(mengeneinheit.Id);
      await loadMengeneinheiten();
      setDeleteDialogOpen(false);
      setSelectedMengeneinheit(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Mengeneinheit');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const mengeneinheit of selectedItems) {
        if (mengeneinheit.Id) {
          await mengeneinheitenApi.delete(mengeneinheit.Id);
        }
      }

      await loadMengeneinheiten();
      setBulkDeleteDialogOpen(false);
      setSelectedItems([]);
      setSelectionMode(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Mengeneinheiten');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedMengeneinheit?.Id) return;

    try {
      setSaving(true);
      setError(null);
      await mengeneinheitenApi.update(selectedMengeneinheit.Id, editFormData);
      await loadMengeneinheiten();
      setEditDialogOpen(false);
      setSelectedMengeneinheit(null);
      setEditFormData({});
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern der Mengeneinheit');
    } finally {
      setSaving(false);
    }
  };

  const openViewDialog = (mengeneinheit: IMengeneinheit) => {
    setSelectedMengeneinheit(mengeneinheit);
    setViewDialogOpen(true);
  };

  const openEditDialog = (mengeneinheit: IMengeneinheit) => {
    setSelectedMengeneinheit(mengeneinheit);
    setEditFormData({
      Einheit: mengeneinheit.Einheit,
      Kurzform: mengeneinheit.Kurzform,
      Kennzahl: mengeneinheit.Kennzahl,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (mengeneinheit: IMengeneinheit) => {
    setSelectedMengeneinheit(mengeneinheit);
    setDeleteDialogOpen(true);
  };

  const columns: IColumn[] = [
    {
      key: 'Einheit',
      name: 'Einheit',
      fieldName: 'Einheit',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'Kurzform',
      name: 'Kurzform',
      fieldName: 'Kurzform',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'Kennzahl',
      name: 'Kennzahl',
      fieldName: 'Kennzahl',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: IMengeneinheit) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="Ansehen"
            onClick={() => openViewDialog(item)}
          />
          {isAdminOrEinkauf && (
            <>
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title="Bearbeiten"
                onClick={() => openEditDialog(item)}
              />
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Löschen"
                onClick={() => openDeleteDialog(item)}
              />
            </>
          )}
        </Stack>
      ),
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Auswählen" Button
    items.push({
      key: 'select',
      text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
      iconProps: { iconName: 'CheckboxComposite' },
      onClick: () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
      },
      disabled: !isAdminOrEinkauf,
    });

    // "Löschen" Button (nur wenn Elemente ausgewählt)
    if (selectionMode && selectedItems.length > 0 && isAdminOrEinkauf) {
      items.push({
        key: 'deleteSelected',
        text: `Löschen (${selectedItems.length})`,
        iconProps: { iconName: 'Delete' },
        onClick: () => setBulkDeleteDialogOpen(true),
      });
    }

    return items;
  }, [selectionMode, selectedItems, isAdminOrEinkauf]);

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedItems(selection.getSelection() as IMengeneinheit[]);
        },
      }),
    []
  );

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '100vh' } }}>
        <Spinner label="Lade Mengeneinheiten..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20 } }}>
      <Text variant="xxLarge">Mengeneinheiten</Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <CommandBar items={commandBarItems} />

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <SearchBox
          placeholder="Mengeneinheiten suchen (Einheit, Kurzform, Kennzahl)..."
          value={searchText}
          onChange={(_, newValue) => setSearchText(newValue || '')}
          styles={{ root: { width: 400 } }}
        />
      </Stack>

      <DetailsList
        items={filteredMengeneinheiten}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selection={selection}
        selectionMode={selectionMode ? SelectionMode.multiple : SelectionMode.none}
        selectionPreservedOnEmptyClick
      />

      {/* View Dialog */}
      <Modal
        isOpen={viewDialogOpen}
        onDismiss={() => setViewDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Mengeneinheit ansehen</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setViewDialogOpen(false)}
          />
        </div>
        <div style={modalContentStyles.body}>
          {selectedMengeneinheit && (
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField label="Einheit" value={selectedMengeneinheit.Einheit} readOnly />
              <TextField label="Kurzform" value={selectedMengeneinheit.Kurzform} readOnly />
              <TextField label="Kennzahl" value={selectedMengeneinheit.Kennzahl} readOnly />
            </Stack>
          )}
        </div>
        <div style={modalContentStyles.footer}>
          <PrimaryButton text="Schließen" onClick={() => setViewDialogOpen(false)} />
        </div>
      </Modal>

      {/* Edit Dialog */}
      <Modal
        isOpen={editDialogOpen}
        onDismiss={() => setEditDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Mengeneinheit bearbeiten</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
          />
        </div>
        <div style={modalContentStyles.body}>
          <Stack tokens={{ childrenGap: 10 }}>
            <TextField
              label="Einheit"
              value={editFormData.Einheit || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Einheit: value }))}
              required
            />
            <TextField
              label="Kurzform"
              value={editFormData.Kurzform || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Kurzform: value }))}
              required
            />
            <TextField
              label="Kennzahl"
              value={editFormData.Kennzahl || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Kennzahl: value }))}
              required
            />
          </Stack>
        </div>
        <div style={modalContentStyles.footer}>
          <DefaultButton text="Abbrechen" onClick={() => setEditDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Speichern" onClick={handleSaveEdit} disabled={saving} />
        </div>
      </Modal>

      {/* Delete Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Mengeneinheit löschen',
          subText: `Möchten Sie die Mengeneinheit "${selectedMengeneinheit?.Einheit}" (${selectedMengeneinheit?.Kurzform}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={() => selectedMengeneinheit && handleDeleteSingle(selectedMengeneinheit)} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        hidden={!bulkDeleteDialogOpen}
        onDismiss={() => setBulkDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Mengeneinheiten löschen',
          subText: `Möchten Sie ${selectedItems.length} Mengeneinheiten wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setBulkDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={handleBulkDelete} disabled={saving} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
